import { useState, useEffect, FC, useRef, ChangeEvent, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Radio } from 'lucide-react';
import { toast } from 'sonner';
import { AlignContainer, BaseButton, BaseSpinner, BaseTextarea } from 'wbs-components-lib';

import { Typography } from '@mui/material';

import { TelegramChatMessageModel } from '@shared/types';

import {
  useGetTelegramChatMessagesQuery,
  useSentAdminMessageMutation,
  useUpdateTelegramChatStatusMutation,
} from '@services';

import { AppDispatch, updateTelegramChatsChatStatus } from '@store';

import { TelegramChatSettingsItem } from './TelegramChatSettingsItem';
import { TelegramChatSettingsChatProps } from './types';

import styles from './TelegramChatSettings.module.css';

const { chatContainer, chatInformationalBox, chatBox, chatInputContainer } = styles;

export const TelegramChatSettings: FC<TelegramChatSettingsChatProps> = ({ telegramChat }) => {
  const dispatch = useDispatch<AppDispatch>();

  const [messages, setMessages] = useState<TelegramChatMessageModel[]>([]);
  const [newMessage, setNewMessage] = useState<string>('');
  const [fileId] = useState<string | null>(null);

  const chatEndRef = useRef<HTMLDivElement | null>(null);

  //const [getFileInfo] = useLazyGetFileInfoQuery();

  const [updateTelegramChatStatus] = useUpdateTelegramChatStatusMutation();

  const { userId } = telegramChat;

  const {
    data: loadedMessages,
    isLoading: isPreviousMessagesLoading,
    isSuccess: isPreviousMessagesLoaded,
  } = useGetTelegramChatMessagesQuery(userId, {
    refetchOnMountOrArgChange: true,
  });
  const [sendAdminMessage] = useSentAdminMessageMutation();

  useEffect(() => {
    if (isPreviousMessagesLoaded) {
      setMessages(loadedMessages);
    }
  }, [loadedMessages, isPreviousMessagesLoaded]);

  useEffect(() => {
    const setChatReadStatus = async () => {
      dispatch(
        updateTelegramChatsChatStatus({
          userId,
          isChatMessagesRead: true,
        })
      );

      await updateTelegramChatStatus({
        userId,
        isChatMessagesRead: true,
      });
    };

    setTimeout(() => {
      if (chatEndRef.current) {
        chatEndRef.current.scrollIntoView({ behavior: 'smooth' });

        setChatReadStatus();
      }
    }, 1000);
  }, [messages]);

  useEffect(() => {
    if (isPreviousMessagesLoaded) {
      const eventSource = new EventSource(`api/v1/admin/telegram-chat/message/${userId}/events`);

      eventSource.onmessage = (event) => {
        const newUserMessage: TelegramChatMessageModel = JSON.parse(event.data);

        setMessages((prev) => [...prev, newUserMessage]);
      };

      eventSource.onerror = () => {
        console.error('SSE connection error');

        eventSource.close();
      };

      return () => {
        eventSource.close();
      };
    }
  }, [isPreviousMessagesLoaded]);

  /*  const handleSendFile = async (fileId: string) => {
    const { name: fileName } = await getFileInfo(fileId).unwrap();

    const fileUrl = getFileSrcByName(fileName);

    await sendAdminMessage({
      payload: {
        message: fileUrl,
        contentType: MessageContentTypesEnum.IMAGE,
      },
      userId: userId,
    }).unwrap();

    setFileId(null);
  };*/

  const handleSendText = async () => {
    await sendAdminMessage({
      message: newMessage,
      userId: userId,
    }).unwrap();

    setNewMessage('');
  };

  const handleMessageSubmit = useCallback(async () => {
    if (newMessage.trim() !== '') {
      await handleSendText();
    }

    if (fileId !== null) {
      //await handleSendFile(fileId);
    }

    toast.success('Message has been sent successfully!');
  }, [newMessage, fileId]);

  const handleMessageChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const message = event.target.value;

    setNewMessage(message);
  };

  const isSubmitDisabled = newMessage.trim() === '' && fileId === null;

  return (
    <div className={chatContainer}>
      {!isPreviousMessagesLoading ? (
        <>
          {messages.length !== 0 ? (
            <div className={chatBox}>
              {messages.map((message) => (
                <TelegramChatSettingsItem telegramChat={telegramChat} key={message.id} message={message} />
              ))}
              <div ref={chatEndRef} />
            </div>
          ) : (
            <div className={chatInformationalBox}>
              <Radio />
              <Typography align="center" fontSize="16px" color="green" alignItems="center" fontWeight={800}>
                There are no messages from user.
              </Typography>
            </div>
          )}
          <div className={chatInputContainer}>
            {/*TODO: <FileSelector setFileId={handleFileIdChange} fileId={fileId} />*/}
            <BaseTextarea
              inputProps={{ maxLength: 600 }}
              title="Message"
              rows={3}
              label="Enter message..."
              value={newMessage}
              onChange={handleMessageChange}
            />
            <BaseButton disabled={isSubmitDisabled} onClick={handleMessageSubmit}>
              Send
            </BaseButton>
          </div>
        </>
      ) : (
        <AlignContainer>
          <BaseSpinner />
        </AlignContainer>
      )}
    </div>
  );
};
